import { Accordion, Button, Dialog, List, Text } from '@/ui'
import { ImageBackgroundColors, TypographyStyles } from '@/styles'
import { BankingApplicationStatus } from '@/features/banking/banking.types'
import { RECOMMENDED_BUSINESS_BANKS } from '@/features/banking/banking.constants'

const APPROVED_ILLUSTRATION =
  'https://heard-images.s3.amazonaws.com/assets/balloons.svg'
const WAITING_ILLUSTRATION =
  'https://heard-images.s3.amazonaws.com/assets/clock.svg'
const DENIED_ILLUSTRATION =
  'https://heard-images.s3.amazonaws.com/assets/plant-monstera.svg'

type ApplicationStatusModalProps = {
  status: BankingApplicationStatus | null
  onClose: () => void
}

const ApprovedModal = ({ onClose }: { onClose: () => void }) => (
  <Dialog open onOpenChange={(val) => !val && onClose()}>
    <Dialog.Content
      heading="You've been approved!"
      illustrationSrc={APPROVED_ILLUSTRATION}
      illustrationAlt="approved"
      illustrationColor={ImageBackgroundColors.GREEN}
      illustrationSize="lg"
      description="Congratulations! Now that you've been approved, let's start pulling in your transactions."
    >
      <Dialog.Footer className="justify-end">
        <Button variant="primary" onClick={onClose}>
          Continue
        </Button>
      </Dialog.Footer>
    </Dialog.Content>
  </Dialog>
)

const WaitingModal = ({ onClose }: { onClose: () => void }) => (
  <Dialog open onOpenChange={(val) => !val && onClose()}>
    <Dialog.Content
      heading="Waiting on application approval"
      illustrationSrc={WAITING_ILLUSTRATION}
      illustrationAlt="waiting"
      illustrationColor={ImageBackgroundColors.YELLOW}
      illustrationSize="lg"
      description="Your application is under review. Watch your inbox for an update. In the meantime, you can connect other bank accounts with business expenses."
    >
      <Dialog.Footer className="justify-end">
        <Button variant="primary" onClick={onClose}>
          Continue
        </Button>
      </Dialog.Footer>
    </Dialog.Content>
  </Dialog>
)

const DeniedModal = ({ onClose }: { onClose: () => void }) => (
  <Dialog open onOpenChange={(val) => !val && onClose()}>
    <Dialog.Content
      heading="We weren't able to approve your application"
      illustrationSrc={DENIED_ILLUSTRATION}
      illustrationAlt="denied"
      illustrationColor={ImageBackgroundColors.ORANGE}
      illustrationSize="lg"
    >
      <Text variant={TypographyStyles.BODY_SM}>
        We weren&apos;t able to approve your application at this time. You can
        reapply later, or explore other banking options.
      </Text>
      <Text variant={TypographyStyles.BODY_SM}>
        In the meantime, you can still connect your existing accounts to Heard.
        We&apos;ll categorize transactions and sort expenses as part of
        bookkeeping, even if they&apos;re from personal accounts.
      </Text>
      <Accordion type="single" collapsible variant="text">
        <Accordion.Item value="recommended-banks">
          <Accordion.Trigger>View recommended banks</Accordion.Trigger>
          <Accordion.Content>
            <Text variant={TypographyStyles.BODY_SM}>
              Many providers start with:
            </Text>
            <List bulleted className="ml-4 mt-2">
              {RECOMMENDED_BUSINESS_BANKS.map((bank) => (
                <List.Item key={bank}>{bank}</List.Item>
              ))}
            </List>
          </Accordion.Content>
        </Accordion.Item>
      </Accordion>
      <Dialog.Footer className="justify-end">
        <Button onClick={onClose}>Close</Button>
      </Dialog.Footer>
    </Dialog.Content>
  </Dialog>
)

export const ApplicationStatusModal = ({
  status,
  onClose,
}: ApplicationStatusModalProps) => {
  if (status === null) return null

  switch (status) {
    case BankingApplicationStatus.Approved:
      return <ApprovedModal onClose={onClose} />
    case BankingApplicationStatus.PendingReview:
      return <WaitingModal onClose={onClose} />
    case BankingApplicationStatus.Denied:
      return <DeniedModal onClose={onClose} />
    case BankingApplicationStatus.AwaitingDocuments:
    case BankingApplicationStatus.Pending:
    case BankingApplicationStatus.Canceled:
      return null
    default:
      return status satisfies never
  }
}
